import { Box } from '@material-ui/core';
import { OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { useMemo, useState } from 'react';

import { CheckoutSessionDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionDetailEntity';
import { AbstractProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/AbstractProcessingStrategyEntity';
import { ProcessingStrategy } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/ProcessingStrategy';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { StoredBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import {
  MerchantPosContextProvider,
  useMerchantPosContext,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/_shared/contexts/MerchantPosContext';
import { useDocumentExtractorUpload } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/hooks/useDocumentExtractorUpload';
import { useMerchantPos } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/hooks/useMerchantPos';
import { MerchantPosForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/MerchantPosForm';
import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';

interface MerchantPosProps {
  opened: boolean;
  setOpened: (opened: boolean) => void;
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  checkoutSessionDetailEntity: CheckoutSessionDetailEntity;
  selectedRole?: OnboardingDetailsRole;
  borrowerOnboardingDetails?: StoredBorrowerOnboardingDetails[];
  onSaveDetails?: (onboardingDetails: StoredBorrowerOnboardingDetails) => Promise<void>;
  beforeSubmit?: () => Promise<void>;
}

export const MerchantPos = ({
  opened,
  setOpened,
  paymentMethodDetailEntity,
  checkoutSessionDetailEntity,
  selectedRole,
  borrowerOnboardingDetails = [],
  onSaveDetails,
  beforeSubmit,
}: MerchantPosProps) => {
  const agent = checkoutSessionDetailEntity.agent;

  if (!opened) {
    return null;
  }

  const isEditing = borrowerOnboardingDetails.length > 0;

  return (
    <MerchantPosContextProvider
      data={{
        agent,
        selectedRole,
        isEditing,
        paymentMethodDetailEntity,
        checkoutSessionDetailEntity,
        borrowerOnboardingDetails,
        onSaveDetails,
        beforeSubmit,
      }}
    >
      <MerchantPosDialog opened={opened} setOpened={setOpened}>
        <MerchantPosForm />
      </MerchantPosDialog>
    </MerchantPosContextProvider>
  );
};

interface MerchantPosDialogProps {
  opened: boolean;
  setOpened: (opened: boolean) => void;
  children: React.ReactElement;
}

const MerchantPosDialog = ({ opened, setOpened, children }: MerchantPosDialogProps) => {
  const {
    selectedRole,
    borrowerOnboardingDetails,
    disableSaveButton,
    paymentMethodDetailEntity,
    isEditing,
    onSave,
    beforeSubmit,
    extractedFiles,
  } = useMerchantPosContext();

  const { createApplications, setSelectedRole } = useMerchantPos();
  const { uploadExtractedFiles } = useDocumentExtractorUpload();

  const [loading, setLoading] = useState(false);
  const [showProcessingStrategy, setShowProcessingStrategy] = useState(false);

  const disabled = useMemo(
    () => !!selectedRole || borrowerOnboardingDetails.length === 0,
    [selectedRole, borrowerOnboardingDetails],
  );

  const onSubmit = async () => {
    try {
      await createApplications();
      setShowProcessingStrategy(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (beforeSubmit) {
        await beforeSubmit();
      }
      await onSubmit();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave();
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onProcessingCompleted = async () => {
    const loanId = paymentMethodDetailEntity.paymentMethodId;
    if (extractedFiles.length > 0 && loanId) {
      await uploadExtractedFiles(loanId, extractedFiles);
    }
    setLoading(false);
  };

  const onChoosingCompleted = async (processingStrategy: AbstractProcessingStrategyEntity) => {
    setLoading(false);
    if (processingStrategy.loanApplications && processingStrategy.loanApplications.length > 1) {
      setTimeout(() => setOpened(false), 1000);
    }
  };

  const onError = () => {
    setLoading(false);
    setOpened(false);
  };

  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      dialogProps={{ maxWidth: false }}
      paperProps={{ maxWidth: 880 }}
      buttons={
        <>
          {showProcessingStrategy && (
            <StandardButton
              disabled={loading}
              onClick={async () => {
                setShowProcessingStrategy(false);
                setOpened(false);
                setLoading(false);
              }}
            >
              <Text tId={'CLOSE'} />
            </StandardButton>
          )}
          {!showProcessingStrategy && (
            <>
              {borrowerOnboardingDetails.length === 0 || !selectedRole || isEditing ? (
                <StandardButton disabled={loading} onClick={() => setOpened(false)}>
                  <Text tId={'CANCEL'} />
                </StandardButton>
              ) : (
                <StandardButton disabled={loading} onClick={() => setSelectedRole(undefined)}>
                  <Text tId={'BACK'} />
                </StandardButton>
              )}
              {selectedRole ? (
                <StandardButton main disabled={disableSaveButton} loading={loading} onClick={handleSave}>
                  <Text tId="SAVE" />
                </StandardButton>
              ) : (
                <StandardButton main disabled={loading || disabled} loading={loading} onClick={handleSubmit}>
                  <Text tId="PROCESS" />
                </StandardButton>
              )}
            </>
          )}
        </>
      }
      dialogProps={{
        maxWidth: showProcessingStrategy ? 'sm' : 'md',
        fullWidth: true,
      }}
    >
      {showProcessingStrategy ? (
        <div>
          <Box mb={4}>
            <Font variant="h3" color="title">
              <Text strong tId="PROCESSING_STRATEGY" />
            </Font>
          </Box>
          <ProcessingStrategy
            paymentMethodDetailEntity={paymentMethodDetailEntity}
            onProcessingCompleted={onProcessingCompleted}
            onChoosingCompleted={onChoosingCompleted}
            onError={onError}
            options={{ showStatusMessage: true }}
          />
        </div>
      ) : (
        children
      )}
    </NemuruDialog>
  );
};
